/**
 * NOTIFICATIONS REDUCER
 *
 * Client-side mirror of the durable notification inbox (the bell / unread count /
 * history) served by the LOS `/notifications` endpoints. The list is hydrated on
 * login and kept fresh by two paths:
 *   - live Web PubSub events (RealtimeProvider → notificationPrepend), and
 *   - polling fallback (useNotificationsHook → notificationsHydrate) for when the
 *     realtime transport isn't configured / connected.
 *
 * Rows use the LOS inbox shape: { _id, recipient, type, severity, dealId, title,
 * body, data, actor, createdAt, readAt }. Live Web PubSub events arrive in the
 * §9 envelope ({ notificationId, type, severity, title, … }); `notificationPrepend`
 * normalizes both into one shape keyed by `_id`.
 */

const notificationsInit = {
  items: [], // inbox rows, newest first
  unread: 0,
  loaded: false, // first hydrate completed (controls "loading" vs "empty" UI)
  hasMore: true, // pagination: more older rows may exist
  // Preferences (per-type channel matrix + severity floor + quiet hours).
  prefs: null,
  prefsMeta: { severities: [], defaultTypeChannels: {} },
  prefsLoaded: false,
  // Realtime transport status: idle | connecting | connected | disconnected | unconfigured.
  realtime: "idle",
};

// Normalize a live Web PubSub envelope OR an inbox row into a single row shape.
const normalizeRow = (n) => {
  const id = n._id || n.notificationId || n.id;
  return {
    ...n,
    _id: id,
    readAt: n.readAt ?? null,
    createdAt: n.createdAt || n.at || new Date().toISOString(),
  };
};

const notificationsReducer = (state = notificationsInit, action) => {
  switch (action.type) {
    case "NOTIFICATIONS_HYDRATE": {
      const items = (action.payload.items || []).map(normalizeRow);
      return {
        ...state,
        items,
        unread:
          action.payload.unread !== undefined
            ? action.payload.unread
            : state.unread,
        loaded: true,
        hasMore: action.payload.hasMore ?? state.hasMore,
      };
    }

    case "NOTIFICATIONS_APPEND": {
      // Older rows (pagination) appended to the end, de-duped by _id.
      const incoming = (action.payload.items || []).map(normalizeRow);
      const seen = new Set(state.items.map((i) => i._id));
      const merged = [...state.items];
      incoming.forEach((row) => {
        if (!seen.has(row._id)) merged.push(row);
      });
      return {
        ...state,
        items: merged,
        hasMore: action.payload.hasMore ?? state.hasMore,
      };
    }

    case "NOTIFICATION_PREPEND": {
      // A single newly-arrived notification (live event). Skip if we already
      // have it (reconnect/replay dedupe) — match the inbox row by _id.
      const row = normalizeRow(action.payload);
      if (!row._id) return state;
      if (state.items.some((i) => i._id === row._id)) return state;
      return {
        ...state,
        items: [row, ...state.items].slice(0, 300),
        unread: row.readAt ? state.unread : state.unread + 1,
      };
    }

    case "NOTIFICATIONS_SET_UNREAD":
      return { ...state, unread: Math.max(0, action.payload | 0) };

    case "NOTIFICATION_MARK_READ": {
      let changed = false;
      const items = state.items.map((i) => {
        if (i._id === action.payload && !i.readAt) {
          changed = true;
          return { ...i, readAt: new Date().toISOString() };
        }
        return i;
      });
      return {
        ...state,
        items,
        unread: changed ? Math.max(0, state.unread - 1) : state.unread,
      };
    }

    case "NOTIFICATIONS_MARK_ALL_READ": {
      const now = new Date().toISOString();
      return {
        ...state,
        items: state.items.map((i) => (i.readAt ? i : { ...i, readAt: now })),
        unread: 0,
      };
    }

    case "NOTIFICATIONS_SET_PREFS":
      return {
        ...state,
        prefs: action.payload.preferences ?? action.payload,
        prefsMeta: {
          severities:
            action.payload.severities ?? state.prefsMeta.severities,
          defaultTypeChannels:
            action.payload.defaultTypeChannels ??
            state.prefsMeta.defaultTypeChannels,
        },
        prefsLoaded: true,
      };

    case "NOTIFICATIONS_SET_REALTIME_STATUS":
      return { ...state, realtime: action.payload };

    case "NOTIFICATIONS_CLEAR":
      return notificationsInit;

    default:
      return state;
  }
};

// ─── ACTION CREATORS ─────────────────────────────────────────────

export const notificationsHydrate = ({ items, unread, hasMore } = {}) => ({
  type: "NOTIFICATIONS_HYDRATE",
  payload: { items, unread, hasMore },
});

export const notificationsAppend = ({ items, hasMore } = {}) => ({
  type: "NOTIFICATIONS_APPEND",
  payload: { items, hasMore },
});

export const notificationPrepend = (notification) => ({
  type: "NOTIFICATION_PREPEND",
  payload: notification,
});

export const notificationsSetUnread = (count) => ({
  type: "NOTIFICATIONS_SET_UNREAD",
  payload: count,
});

export const notificationMarkRead = (id) => ({
  type: "NOTIFICATION_MARK_READ",
  payload: id,
});

export const notificationsMarkAllRead = () => ({
  type: "NOTIFICATIONS_MARK_ALL_READ",
});

export const notificationsSetPrefs = (payload) => ({
  type: "NOTIFICATIONS_SET_PREFS",
  payload,
});

export const notificationsSetRealtimeStatus = (status) => ({
  type: "NOTIFICATIONS_SET_REALTIME_STATUS",
  payload: status,
});

export const notificationsClear = () => ({ type: "NOTIFICATIONS_CLEAR" });

export default notificationsReducer;
